import { useCallback } from 'react';
import classNames from 'classnames';
import { ComponentDesignType, ComponentType, GetPageComponentItemViewModel, GetPageComponentViewModel, ProductViewModel } from '@/types/cml-api';
import { sendCtaClickEvent } from '@/features/analytics/datalayer/datalayer';
import { AppNextLink } from '@/features/app-next-link';
import { Button } from '@/features/button';
import { NavigationCarousel } from '@/features/carousel';
import { CarouselArrowType } from '@/features/carousel/types';
import { IconArrowLeftCircle, IconArrowSquareLeft } from '@/features/icons';
import { useMobileDetect } from '@/features/mobile-detect-context/mobile-detect-provider';
import { HomePageCard } from '@/features/product-card';
import { HomePageCardProps } from '@/features/product-card/types';
import { generateHomepageCardProps } from '@/features/product-card/utils/generate-homepage-card-props';
import { Webengage } from '@/features/webengage';
import { PUBLIC_DOMAIN_URL } from '@/app/_constants/urls';
import PinkBox from './pink-box';
import Skeleton from './skeleton';
import { IsLoadingLazyProps } from '../../type';
interface Props extends IsLoadingLazyProps {
  data: GetPageComponentItemViewModel;
  carouselIsLoaded: boolean;
  isPinkBox: boolean;
  setCarouselIsLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  componentData: GetPageComponentViewModel;
  showLinkInHeader: boolean;
}
function ProductsListCarousel({
  componentData,
  data,
  isLazy,
  isPinkBox,
  setCarouselIsLoaded,
  showLinkInHeader
}: Props) {
  const {
    isMobile
  } = useMobileDetect();
  const handleLoadedCallback = useCallback((isLoaded: boolean) => {
    setCarouselIsLoaded(isLoaded);
  }, [setCarouselIsLoaded]);
  const handleClick = (type: 'product' | 'show-more-link' | 'banner-link', productData?: ProductViewModel | HomePageCardProps | null, link?: string | null) => {
    const selectedProduct = data?.items?.find(item => item.product?.id === productData?.id);
    const componentId = componentData.id ?? '';
    let elementIndex;
    if (type === 'banner-link') {
      elementIndex = 1;
    } else if (type === 'show-more-link') {
      elementIndex = 0;
    } else {
      const displayOrder = Number(selectedProduct?.displayOrder) || 0;
      const shouldAddOne = componentData?.showBannerInList || isPinkBox ? 1 : 0;
      elementIndex = displayOrder + shouldAddOne;
    }
    const itemId = type === 'banner-link' || type === 'show-more-link' ? componentData?.id || '' : selectedProduct?.id || '';
    const destinationUrl = type === 'banner-link' || type === 'show-more-link' ? link || '' : `${PUBLIC_DOMAIN_URL}/products/${selectedProduct?.product?.slug}` || '';
    let image;
    if (type === 'show-more-link') {
      image = null;
    } else if (type === 'banner-link') {
      image = isMobile ? data?.mobileImageUrl : data?.desktopImageUrl;
    } else {
      image = selectedProduct?.product?.mainImageUrl;
    }
    sendCtaClickEvent({
      componentId: componentId,
      elementType: ComponentType.ProductList,
      elementName: componentData?.sortKey,
      elementIndex: elementIndex,
      itemID: itemId,
      destinationUrl: destinationUrl,
      ImageUrl: image,
      elementGroupName: `${data?.title || ''}-${Number(data?.displayOrder) + 1}`
    });
    Webengage.trackCtaAction({
      component_id: componentId,
      banner_type: ComponentType.ProductList,
      banner_name: componentData?.sortKey ?? '',
      banner_index: elementIndex,
      url_destination: destinationUrl,
      item_id: itemId,
      ...(image ? {
        image_url: image
      } : {})
    });
  };
  return <div className={classNames('flex w-full', {
    'flex flex-col rounded p-0 lg:flex-row': isPinkBox,
    'flex-col gap-1.5 lg:gap-3': !isPinkBox
  }, isPinkBox && isMobile && 'gap-0')} data-sentry-component="ProductsListCarousel" data-sentry-source-file="products-list-carousel.tsx">
            <div className={classNames('relative overflow-hidden', {
      'pl-2 lg:pl-0': showLinkInHeader
    })}>
                <NavigationCarousel options={{
        perView: 'auto',
        mode: 'free-snap',
        spacing: 8
      }} containerClassName={classNames({
        '[&>*:last-child]:pl-2 lg:[&>*:last-child]:pl-0': !showLinkInHeader && data?.link && !isPinkBox
      })} skeleton={<Skeleton isPinkBox={isPinkBox} />} arrowType={isPinkBox ? CarouselArrowType.PinkBox : CarouselArrowType.ProductList} hideLeftArrowOnLastSlide={isPinkBox} loadedCallback={handleLoadedCallback} data-sentry-element="NavigationCarousel" data-sentry-source-file="products-list-carousel.tsx">
                    {isPinkBox && !isMobile && <div className='keen-slider__slide min-w-fit max-w-fit px-0'>
                            <PinkBox deactivationCountdownSecs={componentData?.deactivationCountdownSecs || 0} id={data?.id} componentId={data?.id} href={data.link || ''} elementName={componentData?.sortKey} />
                        </div>}

                    {componentData?.showBannerInList && data.mobileImageUrl && <AppNextLink onClick={() => handleClick('banner-link', null, data?.link)} href={data?.link ?? ''} className={classNames('keen-slider__slide relative h-full min-w-fit max-w-fit overflow-hidden rounded px-0', data?.link ? 'cursor-pointer before:absolute before:bottom-0 before:left-0 before:h-[162px] before:w-full before:bg-gradient-to-t before:from-black/50 before:to-black/0 before:content-[""] lg:before:h-[200px]' : 'cursor-default')}>
                            <img src={isMobile ? data.mobileImageUrl : data.desktopImageUrl} alt={data.title} className={classNames('min-h-[248px] lg:min-h-[308px]', '!w-[132px] object-fill object-bottom lg:!w-[224px]')} />
                            {data?.link && <div className='absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1 text-body-14 text-white'>
                                    <span className='whitespace-nowrap'>مشاهده همه</span>
                                    <IconArrowSquareLeft fontSize={20} />
                                </div>}
                        </AppNextLink>}

                    {data?.items?.map((item, index) => {
          const productData = generateHomepageCardProps(item.product as GetPageComponentItemViewModel, isPinkBox);
          return <div className='keen-slider__slide min-w-fit max-w-fit px-0' key={productData.id || index} onClick={() => handleClick('product', productData, null)}>
                                <HomePageCard index={index} {...productData} className={classNames('min-h-[248px] lg:min-h-[280px]', '!w-[132px] lg:!w-[224px]')} isLazy={isLazy && index >= 6} sortKey={`${ComponentDesignType.PinkBox}-${data?.index}`} componentId={data?.id} itemId={item?.id} showNumber={data?.showNumber} />
                            </div>;
        })}

                    {data?.link && !showLinkInHeader && !(isPinkBox && isMobile) && <AppNextLink onClick={() => handleClick('show-more-link', null, data?.link)} href={data?.link ?? ''} className='keen-slider__slide flex min-w-fit max-w-fit flex-col items-center justify-center rounded border bg-white pr-2 text-primary-main'>
                            <IconArrowLeftCircle className='h-6 w-6 lg:h-8 lg:w-8' />
                            <Button graphicStyle='text' size='small' className='py-1.5 pl-2 pr-0 text-primary-main hover:text-primary-main'>
                                مشاهده همه
                            </Button>
                        </AppNextLink>}
                </NavigationCarousel>
            </div>
        </div>;
}
export default ProductsListCarousel;